<script setup lang="ts">
import { Link } from "@inertiajs/vue3"
import { SbListItem, SbListLabel, SbListSection } from "@unio/saltbox-ui"
import { computed, shallowRef, watch } from "vue"

import { ICON } from "@/lib/icons"
import { itemPath } from "@/routes"
import type { OutingItemSummary } from "@/types/models"

type VerificationResult = { present: boolean } | { quantity: number }

const props = defineProps<{
  item: OutingItemSummary
  phase: "gather" | "return"
  outingStatus: "draft" | "out" | "closed"
  processing?: boolean
  showPack: boolean
  showRemove?: boolean
}>()

const emit = defineEmits<{
  checkIn: [outingItemId: string]
  pack: [item: OutingItemSummary, packed: boolean]
  remove: [outingItemId: string]
  verify: [item: OutingItemSummary, result: VerificationResult]
}>()

const actualQuantity = computed(() =>
  props.phase === "gather" ? props.item.verified_quantity : props.item.returned_quantity,
)
const checked = computed(() =>
  props.phase === "gather"
    ? props.item.verified_at !== null
    : props.item.return_verified_at !== null,
)
const complete = computed(() =>
  props.phase === "gather" ? props.item.verified : props.item.return_complete,
)
const quantity = shallowRef(actualQuantity.value ?? props.item.expected_quantity)
const confirmationLabel = computed(() => (props.phase === "gather" ? "Got it" : "Ready to come in"))
const countStatus = computed(() => {
  if (!checked.value) return `Should be ${props.item.expected_quantity}`
  return `${actualQuantity.value ?? 0} of ${props.item.expected_quantity} confirmed`
})

watch(actualQuantity, (value) => {
  quantity.value = value ?? props.item.expected_quantity
})

function confirmPresent(present: boolean) {
  emit("verify", props.item, { present })
}

function confirmCount() {
  emit("verify", props.item, { quantity: quantity.value })
}
</script>

<template>
  <SbListItem :data-testid="`item-${item.item_name}`" class="flex-wrap sm:flex-nowrap">
    <SbListSection avatar>
      <UAvatar :src="item.item_thumb_url ?? undefined" alt="" size="md" />
    </SbListSection>

    <SbListSection>
      <SbListLabel :lines="2">
        <Link :href="itemPath(item.item_id)" class="focus-visible:outline-primary rounded-sm">
          {{ item.item_name }}
        </Link>
      </SbListLabel>
      <SbListLabel v-if="item.verification_mode === 'count'" caption>
        {{ countStatus }}
      </SbListLabel>
      <SbListLabel v-else-if="checked && !complete" caption>Marked missing</SbListLabel>
      <SbListLabel v-if="!item.owned_by_me" caption>Return to {{ item.owner_name }}</SbListLabel>
    </SbListSection>

    <SbListSection
      v-if="item.verification_mode === 'tick' && !(showPack && item.verified)"
      side
      class="order-4 basis-full items-stretch pl-12 sm:order-none sm:basis-auto sm:items-end sm:pl-0"
    >
      <div data-testid="verification-tick" class="flex min-h-11 items-center justify-end gap-2">
        <UButton
          v-if="!complete"
          data-testid="confirm-missing"
          label="Missing"
          color="error"
          variant="soft"
          size="md"
          class="min-h-11"
          :disabled="processing"
          @click="confirmPresent(false)"
        />
        <UButton
          data-testid="confirm-present"
          :label="confirmationLabel"
          :icon="complete ? ICON.success : ICON.check"
          :color="complete ? 'success' : 'neutral'"
          :variant="complete ? 'soft' : 'solid'"
          size="md"
          class="min-h-11"
          :loading="processing"
          @click="confirmPresent(true)"
        />
      </div>
    </SbListSection>

    <SbListSection
      v-else-if="item.verification_mode === 'count' && !(showPack && item.verified)"
      side
      class="order-4 basis-full items-stretch pl-12 sm:order-none sm:basis-auto sm:items-end sm:pl-0"
    >
      <div data-testid="verification-count" class="flex items-center justify-end gap-2">
        <UInputNumber
          :id="`verification-count-${item.id}`"
          v-model="quantity"
          :min="0"
          :aria-label="`Actual count for ${item.item_name}`"
          :increment="{ class: 'min-h-11 min-w-11' }"
          :decrement="{ class: 'min-h-11 min-w-11' }"
          :ui="{ base: 'min-h-11' }"
          class="w-32"
          size="md"
        />
        <UButton
          data-testid="confirm-count"
          label="Confirm count"
          :icon="complete ? ICON.success : ICON.check"
          :color="complete ? 'success' : 'neutral'"
          :variant="complete ? 'soft' : 'solid'"
          size="md"
          class="min-h-11"
          :loading="processing"
          @click="confirmCount"
        />
      </div>
    </SbListSection>

    <SbListSection
      v-if="showPack && outingStatus === 'draft' && item.verified"
      side
      class="order-4 basis-full items-stretch pl-12 sm:order-none sm:basis-auto sm:items-end sm:pl-0"
    >
      <UButton
        data-testid="pack-item"
        :label="item.packed ? 'Loaded' : 'Ready to go out'"
        :icon="item.packed ? ICON.success : ICON.outing"
        :disabled="!item.verified || processing"
        :color="item.packed ? 'success' : 'neutral'"
        :variant="item.packed ? 'soft' : 'solid'"
        size="md"
        class="min-h-11"
        :loading="processing"
        @click="emit('pack', item, !item.packed)"
      />
    </SbListSection>

    <SbListSection
      v-if="outingStatus === 'closed' && item.status === 'out' && item.verification_mode === 'tick'"
      side
      class="order-4 basis-full items-stretch pl-12 sm:order-none sm:basis-auto sm:items-end sm:pl-0"
    >
      <UButton
        data-testid="check-in-item"
        label="Check in"
        :icon="ICON.loanReturn"
        size="md"
        class="min-h-11"
        :loading="processing"
        @click="emit('checkIn', item.id)"
      />
    </SbListSection>

    <SbListSection v-if="showRemove && outingStatus === 'draft'" side class="order-3 sm:order-none">
      <UButton
        :icon="ICON.delete"
        aria-label="Remove item"
        color="neutral"
        variant="ghost"
        square
        size="md"
        class="min-h-11 min-w-11"
        :disabled="processing"
        @click="emit('remove', item.id)"
      />
    </SbListSection>
  </SbListItem>
</template>
